<script lang="ts">
	import { createCheckoutSession, markActivationGateSeen, setTrialLimited, trackFunnelEvent } from '$lib/api';
	import { trapFocus } from '$lib/actions/trapFocus';

	interface Props {
		open: boolean;
		weeksCount: number;
		sequencesCount: number;
		progressionsCount: number;
		evaluationsCount: number;
		bulletinsCount: number;
		hoursSaved: number;
		onClose: () => void;
	}

	let {
		open,
		weeksCount,
		sequencesCount,
		progressionsCount,
		evaluationsCount,
		bulletinsCount,
		hoursSaved,
		onClose
	}: Props = $props();

	let loadingSubscribe = $state(false);
	let loadingLimited = $state(false);
	let errorMsg = $state('');

	// Le bilan doit refléter TOUTES les productions : un utilisateur actif seulement
	// sur les progressions ou les évaluations voyait auparavant un encadré vide.
	let accomplishments = $derived(
		[
			{ count: weeksCount, one: 'semaine de cahier journal', many: 'semaines de cahier journal' },
			{ count: sequencesCount, one: 'fiche de séquence', many: 'fiches de séquence' },
			{ count: progressionsCount, one: 'progression annuelle', many: 'progressions annuelles' },
			{ count: evaluationsCount, one: 'évaluation', many: 'évaluations' },
			{ count: bulletinsCount, one: 'appréciation de bulletin', many: 'appréciations de bulletin' }
		].filter((item) => item.count > 0)
	);

	let hoursFormatted = $derived(hoursSaved.toLocaleString('fr-FR'));

	async function handleSubscribe() {
		if (loadingSubscribe) return;
		loadingSubscribe = true;
		errorMsg = '';
		trackFunnelEvent('checkout_started', { trigger: 'activation_gate' });
		try {
			const priceId = import.meta.env.VITE_STRIPE_LAUNCH_PRO_PRICE_ID;
			const { url } = await createCheckoutSession(priceId, 'monthly');
			window.location.href = url;
		} catch {
			errorMsg = 'Une erreur est survenue. Veuillez réessayer.';
			loadingSubscribe = false;
		}
	}

	async function handleLimited() {
		if (loadingLimited) return;
		loadingLimited = true;
		errorMsg = '';
		try {
			await setTrialLimited();
			onClose();
		} catch {
			errorMsg = 'Une erreur est survenue.';
		} finally {
			loadingLimited = false;
		}
	}

	$effect(() => {
		if (open) markActivationGateSeen().catch(() => {});
	});

	$effect(() => {
		if (!open) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') onClose();
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	});
</script>

{#if open}
	<div class="fixed inset-0 z-[110] bg-ink/60 backdrop-blur-sm" aria-hidden="true"></div>
	<div
		use:trapFocus
		class="fixed inset-0 z-[110] flex items-center justify-center p-4"
		role="dialog"
		aria-modal="true"
		aria-labelledby="activation-gate-title"
	>
		<div class="w-full max-w-md rounded border border-mist bg-paper shadow-overlay font-body">
			<div class="space-y-4 px-6 pt-6 pb-5">
				<div class="space-y-1.5">
					<h2 id="activation-gate-title" class="font-display text-lg font-titre text-ink">
						Ce que vous avez déjà préparé
					</h2>
				</div>

				{#if accomplishments.length > 0}
					<div class="rounded bg-warm-surface px-4 py-3">
						<ul class="space-y-1.5">
							{#each accomplishments as item (item.one)}
								<li class="flex items-baseline gap-3 text-sm text-ink">
									<span class="min-w-8 text-right font-semibold tabular-nums">{item.count}</span>
									<span>{item.count > 1 ? item.many : item.one}</span>
								</li>
							{/each}
						</ul>
						{#if hoursSaved > 0}
							<p class="mt-3 border-t border-mist pt-3 text-sm text-ink">
								Soit environ <strong class="font-semibold">{hoursFormatted} h</strong> de préparation
								en moins, selon notre estimation (30 minutes par génération, 1 h 30 par semaine de cahier journal générée).
							</p>
						{/if}
					</div>
				{/if}

				<p class="text-sm leading-relaxed text-ink">
					Passez à Pro pour continuer sans limite de générations : 9 € par mois, résiliable en un
					clic, remboursé sous 14 jours si vous changez d'avis.
				</p>

				{#if errorMsg}
					<div class="rounded border border-mist bg-warm-surface p-3 text-sm text-ink" role="alert">
						<strong class="font-semibold text-danger">Erreur.</strong>
						{errorMsg}
					</div>
				{/if}
			</div>

			<footer class="space-y-3 border-t border-mist px-6 py-4">
				<button
					type="button"
					class="flex w-full items-center justify-center gap-2 btn-relief px-4 py-2.5 text-sm font-semibold"
					onclick={handleSubscribe}
					disabled={loadingSubscribe}
				>
					{#if loadingSubscribe}
						<span
							class="size-4 animate-spin rounded-full border-2 border-paper border-t-transparent"
						></span>
						Redirection…
					{:else}
						Passer à Pro — 9 €/mois
					{/if}
				</button>
				<div class="text-center">
					<button
						type="button"
						class="text-sm text-ash transition-colors hover:text-ink disabled:opacity-50"
						onclick={handleLimited}
						disabled={loadingLimited}
					>
						{loadingLimited ? 'Patientez…' : 'Continuer avec accès limité'}
					</button>
				</div>
			</footer>
		</div>
	</div>
{/if}
